import React, { useEffect } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useImportStationTextStore } from '../../../shared/store/importStationTextStore';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useCanShowProjectDetail } from '../../../shared/hooks/useProjectVisibility';
import { useTextBlock } from '../../../shared/hooks/useApi/useTextBlock';
import SimpleTextBlock from '../SimpleTextBlock';
import { TextBlockData } from '../../../shared/dto/EntityRepository';
import { useProjectElementActions } from '../../../shared/hooks/useApi/useProjectElement';
import belowIcon from '../../../asset/icon/below.png';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';

const ImportStationForTextBlockMobile: React.FC = () => {
  const { standardModal, clearModal } = useModalStore();
  const { simpleSubList, selectedSubIdList, clearSelectedSubIdList, clearSimpleList } = useImportStationTextStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { getTextBlockListForImport } = useTextBlock();
  const { addTextBlockInProjectElement } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();

  useEffect(() => {
    if (!standardModal) return;
    const textId = standardModal.value!;
    getTextBlockListForImport(textId);

    return () => {
      clearSimpleList();
    };
  }, []);

  const handleSubmit = async () => {
    if (!project || !standardModal?.title || !standardModal?.value) return;
    if (selectedSubIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await addTextBlockInProjectElement({
      projectElementId: standardModal.title,
      textId: standardModal.value,
      textBlockIds: selectedSubIdList,
    });
  };

  const handleClose = () => {
    clearSelectedSubIdList();
    clearSimpleList();
    clearModal(400);
  };

  if (!canShowProject || !project) return null;

  return (
    <ImportComp>
      <TitleWrapper>
        <Title>{getModalMessage('SELECT_TEXT_BLOCK_TO_ADD')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <ContentWrapper>
        <ContentContainer>
          {simpleSubList.map((tb) => (
            <SimpleTextBlock
              key={tb.id}
              data={tb as TextBlockData}
              projectElementId={standardModal?.title ?? undefined}
            />
          ))}
        </ContentContainer>
      </ContentWrapper>
      <HeadlessBtn
        value={getBtnName('IMPORT')}
        handleClick={handleSubmit}
        StyledBtn={SubmitButton}
      />
    </ImportComp>
  );
};


const ImportComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60vh;
  min-height: 0;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 40px;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    bottom: 0;
    background: linear-gradient(
      to top,
      rgba(228, 228, 228, 0.15),
      transparent
    );
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 15px 0 10px 0;
`;

export default ImportStationForTextBlockMobile;
